import Seo from "@/components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/motion";
import useApi from "@/hooks/useApi";
import { GROUP_STATS, SOLUTIONS, HISTORY } from "@/content/site";
import { brandSlugByName, BRAND_DETAILS } from "@/content/brands";
import LinkedInFollow from "@/components/LinkedInFollow";

const brandHeroImage = (brand, fallback) => {
  const d = BRAND_DETAILS[brandSlugByName[brand?.name]] || {};
  const hero_images = (brand?.hero_images?.length && brand.hero_images) || d.hero_images;
  const hero_image = brand?.hero_image || d.hero_image;
  return (hero_images && hero_images[0]) || hero_image || fallback;
};

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1900";

const HeroLine = ({ children, delay, className = "" }) => (
  <span className="reveal-mask">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => (
  <section data-testid="hero-section" className="pt-[76px]">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 pt-16 md:pt-24 pb-14">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.8 }}
        className="kicker mb-8"
      >
        Which Media · The A-Z of Education
      </motion.p>
      <h1 className="headline-xl text-[9vw] lg:text-[110px] uppercase text-ink">
        <HeroLine delay={0.2}>We are the home</HeroLine>
        <HeroLine delay={0.34}>of education</HeroLine>
        <HeroLine delay={0.48} className="text-primary">intelligence</HeroLine>
      </h1>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
        className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
      >
        <p className="max-w-2xl text-lg md:text-2xl font-medium leading-snug text-foreground/80">
          We build the publications, awards and data platforms that families and institutions trust to
          make the most important decisions in education — across seven international markets and three
          continents.
        </p>
        <div className="flex items-center gap-4 shrink-0">
          <Link to="/brands" data-testid="hero-cta" className="inline-flex items-center gap-2 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full pl-6 pr-5 py-3.5 hover:bg-ink transition-colors">
            Explore our brands <ArrowRight size={16} />
          </Link>
          <Link to="/about" className="inline-flex items-center gap-2 font-sans text-sm font-bold text-ink rounded-full pl-6 pr-5 py-3.5 border-2 border-ink hover:bg-ink hover:text-white transition-colors">
            Who we are
          </Link>
        </div>
      </motion.div>
    </div>
    <FadeUp>
      <div className="ed-img group aspect-[21/9] md:aspect-[21/7] max-h-[560px]">
        <img src={HERO_IMG} alt="Students collaborating with technology to make education decisions" />
      </div>
    </FadeUp>
  </section>
);

const Stats = () => (
  <section data-testid="stats-row" className="bg-ink text-white">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
      <FadeUp>
        <p className="kicker mb-4">By the numbers</p>
        <h2 className="headline text-4xl md:text-6xl uppercase max-w-3xl">
          A decade of defining the category
        </h2>
      </FadeUp>
      <Stagger className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
        {GROUP_STATS.map((s) => (
          <StaggerItem key={s.label} className="border-t-2 border-white/20 pt-5">
            <p className="headline-xl text-6xl md:text-7xl text-primary">{s.value}</p>
            <p className="mt-3 text-base md:text-lg font-bold text-white">{s.label}</p>
            <p className="mt-1 text-sm text-white/55">{s.sub}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Brands = () => {
  const { data } = useApi("/brands");
  const list = data || [];
  return (
    <section data-testid="brands-preview" className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <FadeUp>
          <p className="kicker mb-4">Our Brands</p>
          <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-2xl">
            Brands parents and institutions rely on
          </h2>
        </FadeUp>
        <Link to="/brands" className="group inline-flex items-center gap-2 font-sans text-sm font-bold text-primary">
          See all brands <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((b, i) => (
          <StaggerItem key={b.id}>
            <Link to={brandSlugByName[b.name] ? `/brands/${brandSlugByName[b.name]}` : "/brands"} data-testid={`home-brand-${i}`} className="group block bg-secondary rounded-2xl overflow-hidden h-full hover:shadow-xl transition-shadow duration-300">
              <div className="ed-img aspect-[16/10]">
                <img src={brandHeroImage(b, b.image)} alt={b.name} />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="kicker !text-foreground/50">{b.category}</span>
                  {b.status === "coming_soon" && (
                    <span className="font-sans text-[10px] font-extrabold uppercase tracking-wide bg-primary text-primary-foreground px-2 py-1 rounded-full">Soon</span>
                  )}
                </div>
                <h3 className="headline text-2xl text-ink group-hover:text-primary transition-colors flex items-center gap-1.5">
                  {b.name} <ArrowUpRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/70 line-clamp-3">{b.tagline}</p>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
};

const Mission = () => (
  <section data-testid="mission-band" className="bg-primary text-primary-foreground">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <p className="kicker !text-white/80">Our mission</p>
      </div>
      <div className="lg:col-span-8">
        <FadeUp>
          <p className="headline text-3xl md:text-5xl uppercase leading-[1.05]">
            Every child deserves the right education. Every parent deserves the truth to find it.
          </p>
          <p className="mt-8 max-w-2xl text-lg md:text-xl font-medium text-white/90 leading-relaxed">
            We exist to make the education market transparent, measurable and trustworthy — replacing
            hearsay with evidence, and marketing with independent judgement. It is a mission we have
            pursued relentlessly since 2013, and one that now spans reviews, awards, data and consultancy.
          </p>
          <Link to="/about" className="mt-10 inline-flex items-center gap-2 font-sans text-sm font-bold text-white border-b-2 border-white pb-1 hover:gap-3 transition-all">
            Read our story <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Solutions = () => (
  <section data-testid="solutions-preview" className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
    <FadeUp>
      <p className="kicker mb-4">What we do</p>
      <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-3xl">
        Intelligence for everyone in education
      </h2>
    </FadeUp>
    <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
      {SOLUTIONS.map((s) => (
        <StaggerItem key={s.key} className="border-t-4 border-ink pt-6">
          <p className="kicker mb-3">{s.kicker}</p>
          <h3 className="headline text-2xl md:text-[28px] text-ink mb-4">{s.title}</h3>
          <p className="text-[15px] leading-relaxed text-foreground/70">{s.body}</p>
        </StaggerItem>
      ))}
    </Stagger>
    <FadeUp delay={0.1}>
      <Link to="/solutions" className="mt-12 inline-flex items-center gap-2 font-sans text-sm font-bold bg-ink text-white rounded-full pl-6 pr-5 py-3.5 hover:bg-primary transition-colors">
        Explore our solutions <ArrowRight size={16} />
      </Link>
    </FadeUp>
  </section>
);

const History = () => (
  <section data-testid="history-band" className="bg-secondary">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
      <FadeUp>
        <p className="kicker mb-4">Our history</p>
        <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-3xl">
          A decade connecting families to their future
        </h2>
      </FadeUp>
      <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
        {HISTORY.map((h) => (
          <StaggerItem key={h.year} className="border-l-2 border-primary pl-6">
            <p className="headline text-3xl text-primary">{h.year}</p>
            <h3 className="headline text-xl text-ink mt-2 mb-2">{h.title}</h3>
            <p className="text-[15px] leading-relaxed text-foreground/70">{h.body}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const LatestNews = () => {
  const { data } = useApi("/news");
  const list = (data || []).slice(0, 3);
  return (
    <section data-testid="news-preview" className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
      <div className="flex items-end justify-between gap-6 mb-12">
        <FadeUp>
          <p className="kicker mb-4">Latest News</p>
          <h2 className="headline text-4xl md:text-6xl uppercase text-ink">From the newsroom</h2>
        </FadeUp>
        <Link to="/news" className="group inline-flex items-center gap-2 font-sans text-sm font-bold text-primary">
          All news <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {list.map((n) => (
          <StaggerItem key={n.id}>
            <Link to={n.slug ? `/news/${n.slug}` : "/news"} data-testid={`home-news-${n.slug || n.id}`} className="group block">
              <div className="ed-img aspect-[16/10] rounded-2xl mb-5">
                <img src={n.image || undefined} alt={n.title} />
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="kicker">{n.category}</span>
                <span className="text-xs font-semibold text-foreground/40">{n.date}</span>
              </div>
              <h3 className="headline text-xl text-ink group-hover:text-primary transition-colors leading-tight">
                {n.title}
              </h3>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
};

const CareersCta = () => (
  <section data-testid="careers-cta" className="bg-ink text-white">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
      <FadeUp>
        <p className="kicker mb-4">Careers</p>
        <h2 className="headline text-4xl md:text-6xl uppercase max-w-2xl">
          Build the future of education intelligence
        </h2>
        <p className="mt-6 max-w-xl text-lg text-white/70">
          We hire journalists, analysts and builders who care deeply about getting it right.
        </p>
      </FadeUp>
      <Link to="/careers" className="shrink-0 inline-flex items-center gap-2 font-sans text-base font-bold bg-primary text-primary-foreground rounded-full pl-8 pr-6 py-4 hover:bg-white hover:text-ink transition-colors">
        See open roles <ArrowRight size={18} />
      </Link>
    </div>
  </section>
);

const HOME_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Which Media",
    url: (process.env.REACT_APP_SITE_URL || "https://which-media.com").replace(/\/$/, ""),
    logo: "/logos/whichmedia-white2.png",
    description:
      "Which Media is the home of Education Intelligence — the trusted group behind WhichSchoolAdvisor.com, SchoolsCompared.com, Top Schools Awards, EdinformaPlus, the Education Intelligence Group and WhichUniversityAdvisor.com.",
    sameAs: [
      "https://whichschooladvisor.com",
      "https://schoolscompared.com",
      "https://topschoolsawards.com/",
      "https://educationintelligenceunit.com",
    ],
    subOrganization: [
      { "@type": "Organization", name: "WhichSchoolAdvisor.com", url: "https://whichschooladvisor.com" },
      { "@type": "Organization", name: "SchoolsCompared.com", url: "https://schoolscompared.com" },
      { "@type": "Organization", name: "Top Schools Awards", url: "https://topschoolsawards.com/" },
      { "@type": "Organization", name: "EdinformaPlus" },
      { "@type": "Organization", name: "Education Intelligence Group", url: "https://educationintelligencegroup.com" },
      { "@type": "Organization", name: "WhichUniversityAdvisor.com" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Which Media",
    url: (process.env.REACT_APP_SITE_URL || "https://which-media.com").replace(/\/$/, ""),
  },
];

const Home = () => (
  <div data-testid="home-page">
    <Seo
      path="/"
      description="Which Media is the home of Education Intelligence — the trusted group behind WhichSchoolAdvisor.com, SchoolsCompared.com, Top Schools Awards, EdinformaPlus, the Education Intelligence Group and WhichUniversityAdvisor.com."
      jsonLd={HOME_JSONLD}
    />
    <Hero />
    <Stats />
    <Brands />
    <Mission />
    <Solutions />
    <History />
    <LatestNews />
    <LinkedInFollow />
    <CareersCta />
  </div>
);

export default Home;
